import React, { useEffect, useState } from "react";
import { X, ExternalLink } from "lucide-react";
import { useLang } from "@/context/LangContext";

/**
 * iOS Safari nudge banner.
 *
 * Detects when the site is opened on an iPhone/iPad inside a NON-Safari browser
 * (Chrome, Firefox, Facebook/Instagram in-app WebView, ...) and offers a
 * one-tap button to reopen the URL in Safari. This is critical because
 * iOS PWA install ("Add to Home Screen") + Web Push only work in Safari.
 *
 * The button uses the `x-safari-https://` URL scheme which iOS resolves as
 * "open in Safari".  When it's not honoured (rare — e.g. very old iOS),
 * we also fall back to copying the URL + a short instructions link.
 *
 * Dismissable for 7 days via localStorage.
 */
const STORAGE_KEY = "tigik_ios_safari_banner_hidden_until";

const TR = {
  sk: {
    title: "Otvor Tigik v Safari",
    sub: "iPhone: Safari = plné funkcie · notifikácie · inštalácia",
    open: "Otvoriť v Safari",
    hint: "Ak sa nič nestane: klik na … dolu → 'Otvoriť v prehliadači Safari'",
  },
  en: {
    title: "Open Tigik in Safari",
    sub: "iPhone: Safari = full features · notifications · install",
    open: "Open in Safari",
    hint: "If nothing happens: tap the ⋯ button below → 'Open in Safari'",
  },
  cs: {
    title: "Otevřít Tigik v Safari",
    sub: "iPhone: Safari = plné funkce · notifikace · instalace",
    open: "Otevřít v Safari",
    hint: "Pokud se nic nestane: klik na … dole → 'Otevřít v Safari'",
  },
  de: {
    title: "Tigik in Safari öffnen",
    sub: "iPhone: Safari = volle Funktionen · Benachrichtigungen · Installation",
    open: "In Safari öffnen",
    hint: "Wenn nichts passiert: unten auf ⋯ tippen → 'In Safari öffnen'",
  },
  pl: {
    title: "Otwórz Tigik w Safari",
    sub: "iPhone: Safari = pełne funkcje · powiadomienia · instalacja",
    open: "Otwórz w Safari",
    hint: "Jeśli nic się nie stanie: kliknij ⋯ na dole → 'Otwórz w Safari'",
  },
  fr: {
  title: "Ouvre Tigik dans Safari",
  sub: "iPhone : Safari = toutes les fonctionnalités · notifications · installation",
  open: "Ouvrir dans Safari",
  hint: "Si rien ne se passe : clique sur … en bas → 'Ouvrir dans le navigateur Safari'",
},
};

function detectIosNonSafari() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iOS 13+ iPad reports as MacIntel + touch — cover that too
  const isIos =
    /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes("Mac") && "ontouchend" in document);
  if (!isIos) return false;

  // Skip when already installed as PWA (standalone).
  if (window.matchMedia?.("(display-mode: standalone)")?.matches) return false;
  if (window.navigator.standalone) return false;

  // Detect NON-Safari on iOS. Real Safari has "Safari/" AND NOT "CriOS" / "FxiOS" / "EdgiOS" / "OPR/".
  const hasSafari = /Safari/i.test(ua);
  const isChromeIos = /CriOS/i.test(ua);
  const isFirefoxIos = /FxiOS/i.test(ua);
  const isEdgeIos = /EdgiOS/i.test(ua);
  const isOperaIos = /OPiOS|OPT/i.test(ua);
  const isFbBrowser = /FBAN|FBAV|Instagram|Line|TikTok|WhatsApp|Snapchat|Telegram/i.test(ua);
  const isNonSafariBrowser = isChromeIos || isFirefoxIos || isEdgeIos || isOperaIos || isFbBrowser;

  // If UA has ANY non-Safari signal, or has NO "Safari/" at all → treat as non-Safari
  return isNonSafariBrowser || !hasSafari;
}

export default function IOSSafariBanner() {
  const { lang } = useLang();
  const tr = TR[lang] || TR.sk;
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!detectIosNonSafari()) return;
    const hiddenUntil = localStorage.getItem(STORAGE_KEY);
    if (hiddenUntil && Date.now() < parseInt(hiddenUntil, 10)) return;
    setShow(true);
  }, []);

  const openInSafari = () => {
    // iOS URL scheme that forces Safari to open the URL.
    // `x-safari-https://` works on iOS Chrome, Firefox and most in-app browsers.
    // Fallback: navigate to a plain https:// (user still has to tap "Open in Safari"
    // from the … menu, but the hint text below explains this).
    const target = window.location.href.replace(/^https?:\/\//, "");
    const safariUrl = `x-safari-https://${target}`;
    // Use location.href — some in-app browsers block window.open for custom schemes.
    try {
      window.location.href = safariUrl;
    } catch (_) {
      window.location.href = window.location.href;
    }
  };

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(STORAGE_KEY, String(Date.now() + 7 * 24 * 3600 * 1000));
  };

  if (!show) return null;

  return (
    <div
      data-testid="ios-safari-banner"
      className="sticky top-0 z-[60] w-full border-b border-gold-700/60 bg-gradient-to-r from-zinc-950 via-zinc-900 to-zinc-950 backdrop-blur-md shadow-[0_4px_18px_-4px_rgba(212,175,55,0.35)]"
    >
      <div className="max-w-6xl mx-auto flex items-center gap-3 px-3 py-2.5">
        <div className="hidden xs:flex w-8 h-8 items-center justify-center bg-gold-500/15 border border-gold-700 flex-shrink-0">
          <svg viewBox="0 0 24 24" className="w-4 h-4 text-gold-400" fill="currentColor" aria-hidden="true">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.53 6.53l-4.5 6.75-6.75 4.5 4.5-6.75 6.75-4.5z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm text-white leading-tight" data-testid="ios-safari-banner-title">
            {tr.title}
          </div>
          <div className="text-[10.5px] text-zinc-400 font-mono uppercase tracking-wider truncate">
            {tr.sub}
          </div>
        </div>
        <button
          type="button"
          data-testid="ios-safari-banner-open"
          onClick={openInSafari}
          className="wb-btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] whitespace-nowrap"
          title={tr.hint}
        >
          <ExternalLink className="w-3.5 h-3.5" />
          {tr.open}
        </button>
        <button
          type="button"
          data-testid="ios-safari-banner-dismiss"
          onClick={dismiss}
          aria-label="Zavrieť"
          className="text-zinc-500 hover:text-white p-1 flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
